import React, { useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
} from 'antd';
import type { Dayjs } from 'dayjs';
import ErrorState from '../../../commonComponents/ErrorState';
import { FIELD_LIMITS, TERMS_VERSIONING_NOTICE } from '../constants';
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT } from '../utils/dateRange';
import { resolveCurrentTerms } from '../utils/terms';
import {
  FilterGroup,
  FilterLabel,
  InlineNotice,
  SectionCard,
  SectionTitle,
} from '../Styles';
import { useCreatorOptions, useStoreNamesById, useStoreOptions } from '../hooks/useSelectOptions';
import { useCreator } from '../hooks/useCreatorQueries';
import { useCreateTerms } from '../hooks/useCreatorMutations';
import CurrentTermsPanel from './CurrentTermsPanel';
import TermsHistoryTable from './TermsHistoryTable';
import type { AllowanceType, CreateTermsPayload } from '../types';

const { RangePicker } = DatePicker;

interface TermsFormValues {
  allowanceType: AllowanceType;
  allowanceValue: number;
  minSubtotal?: number;
  maxCustomerShare?: number;
  maxCreatorShare?: number;
  maxActiveCodes?: number;
  storeIds?: string[];
  period: [Dayjs, Dayjs];
}

/**
 * §1.4 — set commission terms.
 *
 * Terms are immutable and versioned: submitting always creates a NEW row that
 * immediately supersedes the current one. There is no PATCH, so nothing here is
 * framed as editing — the current terms are shown read-only above the form, and the
 * form is explicitly labelled as creating a new version.
 *
 * A creator needs active terms before they can create a discount code at all, so
 * "no terms yet" is a normal state this screen has to handle, not an error.
 */
const TermsTab: React.FC = () => {
  const [form] = Form.useForm<TermsFormValues>();
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>();

  const { options: creatorOptions, loading: creatorsLoading } = useCreatorOptions();

  const {
    data: creator,
    isLoading: creatorLoading,
    isError: creatorError,
    refetch: refetchCreator,
  } = useCreator(selectedCreatorId);

  const { options: storeOptions } = useStoreOptions();
  const storeNamesById = useStoreNamesById();
  const { mutate: createTerms, isPending } = useCreateTerms();

  // `allowanceType` gates the value cap, so the validator has to react to it.
  const allowanceType = Form.useWatch('allowanceType', form);

  const currentTerms = useMemo(
    () => resolveCurrentTerms(creator?.terms),
    [creator]
  );

  const handleSubmit = (values: TermsFormValues) => {
    if (!selectedCreatorId) return;

    const [startsAt, endsAt] = values.period;

    const payload: CreateTermsPayload = {
      allowanceType: values.allowanceType,
      allowanceValue: values.allowanceValue,
      startsAt: startsAt.format(API_DATE_FORMAT),
      endsAt: endsAt.format(API_DATE_FORMAT),
    };

    // Optional numerics: only send what was actually filled in, so an untouched
    // field doesn't overwrite the server's own default (e.g. maxActiveCodes = 5).
    if (values.minSubtotal != null) payload.minSubtotal = values.minSubtotal;
    if (values.maxCustomerShare != null) payload.maxCustomerShare = values.maxCustomerShare;
    if (values.maxCreatorShare != null) payload.maxCreatorShare = values.maxCreatorShare;
    if (values.maxActiveCodes != null) payload.maxActiveCodes = values.maxActiveCodes;
    // Omitted or empty means "all stores" — send it only when scoping.
    if (values.storeIds?.length) payload.storeIds = values.storeIds;

    createTerms(
      { id: selectedCreatorId, payload },
      { onSuccess: () => form.resetFields() }
    );
  };

  return (
    <>
      <FilterGroup style={{ maxWidth: 380, marginBottom: 20 }}>
        <FilterLabel>Creator</FilterLabel>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Select a creator"
          loading={creatorsLoading}
          options={creatorOptions}
          value={selectedCreatorId}
          onChange={setSelectedCreatorId}
          notFoundContent={creatorsLoading ? <Spin size="small" /> : 'No creators yet'}
        />
      </FilterGroup>

      {!selectedCreatorId ? (
        <InlineNotice>
          Select a creator to view their current commission terms and set a new version.
        </InlineNotice>
      ) : creatorError ? (
        <ErrorState
          title="Could not load this creator"
          message="Please try again."
          onRetry={() => refetchCreator()}
        />
      ) : creatorLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <>
          <SectionCard>
            <CurrentTermsPanel terms={currentTerms} storeNamesById={storeNamesById} />
          </SectionCard>

          <SectionCard>
            <SectionTitle>Set New Terms</SectionTitle>
            <InlineNotice style={{ marginTop: 12 }}>{TERMS_VERSIONING_NOTICE}</InlineNotice>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              disabled={isPending}
              initialValues={{
                allowanceType: 'percent',
                maxActiveCodes: FIELD_LIMITS.DEFAULT_MAX_ACTIVE_CODES,
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Allowance type"
                    name="allowanceType"
                    rules={[{ required: true, message: 'Please select a type' }]}
                  >
                    <Select
                      options={[
                        { value: 'percent', label: 'Percent (%)' },
                        { value: 'fixed', label: 'Fixed amount ($)' },
                      ]}
                      onChange={() => form.validateFields(['allowanceValue'])}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    label="Allowance value"
                    name="allowanceValue"
                    dependencies={['allowanceType']}
                    extra={
                      allowanceType === 'percent'
                        ? `Capped at ${FIELD_LIMITS.PERCENT_ALLOWANCE_MAX}%`
                        : 'No cap for fixed amounts'
                    }
                    rules={[
                      { required: true, message: 'Please enter a value' },
                      {
                        // The API rejects percent > 100 with a 400; fail here first.
                        validator: (_, value) => {
                          if (value == null) return Promise.resolve();
                          if (value < 0) {
                            return Promise.reject(new Error('Must be zero or greater'));
                          }
                          if (
                            form.getFieldValue('allowanceType') === 'percent' &&
                            value > FIELD_LIMITS.PERCENT_ALLOWANCE_MAX
                          ) {
                            return Promise.reject(
                              new Error(
                                `Percent allowance cannot exceed ${FIELD_LIMITS.PERCENT_ALLOWANCE_MAX}`
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      prefix={allowanceType === 'percent' ? undefined : '$'}
                      suffix={allowanceType === 'percent' ? '%' : undefined}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    label="Max active codes"
                    name="maxActiveCodes"
                    extra="How many active codes this creator may hold at once."
                  >
                    <InputNumber style={{ width: '100%' }} min={1} precision={0} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Minimum order subtotal"
                    name="minSubtotal"
                    extra="Order subtotal floor before a code can be applied."
                  >
                    <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Max customer share" name="maxCustomerShare">
                    <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Max creator share" name="maxCreatorShare">
                    <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Active period"
                name="period"
                extra="The end date must be after the start date."
                rules={[
                  { required: true, message: 'Please select a start and end date' },
                  {
                    validator: (_, value: [Dayjs, Dayjs] | undefined) => {
                      if (!value?.[0] || !value?.[1]) return Promise.resolve();
                      return value[1].isAfter(value[0])
                        ? Promise.resolve()
                        : Promise.reject(new Error('End date must be after the start date'));
                    },
                  },
                ]}
              >
                <RangePicker style={{ width: '100%' }} format={DISPLAY_DATE_FORMAT} />
              </Form.Item>

              <Form.Item
                label="Stores"
                name="storeIds"
                extra="Leave empty to apply these terms to all stores."
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All stores"
                  optionFilterProp="label"
                  options={storeOptions}
                />
              </Form.Item>

              <Divider />

              <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={() => form.resetFields()} disabled={isPending}>
                  Reset
                </Button>
                <Button type="primary" loading={isPending} onClick={() => form.submit()}>
                  Create New Terms Version
                </Button>
              </Space>
            </Form>
          </SectionCard>

          <SectionCard>
            <TermsHistoryTable
              terms={creator?.terms}
              currentTermsId={currentTerms?.id}
              loading={creatorLoading}
            />
          </SectionCard>
        </>
      )}
    </>
  );
};

export default TermsTab;
